import {
  AppAlert,
  AppCheckbox,
  AppCombobox,
  AppDialog,
  AppInput,
  AppTextarea,
  AppTextLink,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Users } from 'lucide-react'
import {
  FIELD_MAX_LENGTHS,
  INFORMATION_SOURCE_OPTIONS,
  OPPORTUNITY_TYPE_IDS,
  type OpportunityTypeId,
  SALESFORCE_PROJECT_MEMBER_TYPE_IDS,
} from 'shared'
import { ProjectSelector } from './components/ProjectSelector'
import { useCopyDialog } from './hooks/useCopyDialog'

/**
 * CopyDialog コンポーネントの Props
 */
interface CopyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunity: {
    id: number
    salesforce_project_id: number | null
    title: string
    information_source?: string | null
    description?: string | null
    is_expansion?: boolean
    start_on?: string | null
    contact_name?: string | null
  } | null
  opportunityTypeId?: OpportunityTypeId
}

/**
 * CopyDialog - オポチュニティコピーダイアログ
 *
 * 既存のオポチュニティを元に新規オポチュニティを作成するダイアログコンポーネント
 */
export function CopyDialog({
  open,
  onOpenChange,
  opportunity,
  opportunityTypeId = OPPORTUNITY_TYPE_IDS.CAPTAIN,
}: CopyDialogProps) {
  const {
    formData,
    updateFormData,
    errors,
    hasErrors,
    chatMembersData,
    isChatMembersLoading,
    isPending,
    dialogTitle,
    dateConstraints,
    handleDescriptionChange,
    handleSubmit,
    handleClose,
  } = useCopyDialog({
    open,
    onOpenChange,
    opportunity,
    opportunityTypeId,
  })

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      isPending={isPending}
      className='sm:max-w-4xl max-h-[90vh] flex flex-col'
      footerOptions={[
        { label: 'キャンセル', onClick: handleClose, variant: 'outline' },
        {
          label: '作成',
          onClick: handleSubmit,
          disabled: hasErrors,
          isPending: isPending,
          isPendingText: '作成中...',
        },
      ]}
    >
      <AppAlert type='warning'>機密情報にあたる文言入力はお控えいただけますようご留意ください</AppAlert>

      <div className='flex-1 overflow-y-auto space-y-4'>
        {/* 必須項目 */}
        <div className='space-y-4 p-4'>
          <h2 className='font-semibold'>必須項目</h2>

          <AppInput
            id='title'
            label='タイトル'
            required
            value={formData.title}
            onChange={(e) => updateFormData({ title: e.target.value })}
            placeholder='オポチュニティのタイトル'
            maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_TITLE}
            showCharCount
            error={errors.title}
          />

          <div className='space-y-2'>
            {opportunity ? (
              <AppInput
                value={`案件ID: ${formData.salesforceProjectId}`}
                label='オポチュニティを紐づける案件'
                disabled
                required
                helpText='コピー元の案件が自動的に設定されます'
              />
            ) : (
              <ProjectSelector
                value={formData.salesforceProjectId || null}
                required
                onChange={(projectId) => {
                  updateFormData({ salesforceProjectId: projectId || 0 })
                }}
                memberTypeId={
                  opportunityTypeId === OPPORTUNITY_TYPE_IDS.CAPTAIN
                    ? SALESFORCE_PROJECT_MEMBER_TYPE_IDS.CAPTAIN
                    : SALESFORCE_PROJECT_MEMBER_TYPE_IDS.SALES_MEMBER
                }
                placeholder='プロジェクトを検索（ANOでの検索可）'
                error={errors.salesforceProjectId}
              />
            )}
            {/* TODO: リンクボタンが浮いて見える */}
            {formData.salesforceProjectId > 0 && (
              <AppTextLink href={`/pks/salesforce/project/${formData.salesforceProjectId}`} external>
                基本情報詳細画面を表示
              </AppTextLink>
            )}

            {!opportunity && formData.salesforceProjectId > 0 && (
              <div className='mt-3 p-3 border rounded-md bg-muted/30'>
                <div className='flex items-center gap-2 mb-2'>
                  <Users className='h-4 w-4 text-muted-foreground' />
                  <span className='text-sm font-medium'>チャットメンバー</span>
                  {chatMembersData && (
                    <span className='text-xs text-muted-foreground'>({chatMembersData.total}名)</span>
                  )}
                </div>
                {isChatMembersLoading ? (
                  <p className='text-xs text-muted-foreground'>読み込み中...</p>
                ) : chatMembersData && chatMembersData.members.length > 0 ? (
                  <div className='space-y-1 max-h-32 overflow-y-auto'>
                    {chatMembersData.members.map((member) => (
                      <div
                        key={member.memberId}
                        className='flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-muted/50'
                      >
                        <span className='font-medium'>{member.memberName}</span>
                        <span className='text-muted-foreground'>
                          {member.divisionName} / {member.roleName}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='text-xs text-muted-foreground'>チャットメンバーがいません</p>
                )}
                <p className='text-xs text-muted-foreground mt-2'>
                  オポチュニティ作成後、上記メンバーにチャット通知が送信されます
                </p>
              </div>
            )}
          </div>

          <AppCheckbox
            label='上記案件の拡張案件に該当する'
            checked={formData.isExpansion}
            onCheckedChange={(checked) => updateFormData({ isExpansion: checked as boolean })}
          />
        </div>

        {/* 補足項目 */}
        <div className='space-y-4 p-4'>
          <h2 className='font-semibold'>補足項目</h2>

          <AppCombobox
            value={formData.informationSource || ''}
            onValueChange={(value) => updateFormData({ informationSource: value || null })}
            options={INFORMATION_SOURCE_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
            field={{
              label: 'オポチュニティの情報源',
              error: errors.informationSource,
              helpText: 'この情報の入手元を選択もしくは自由入力してください',
            }}
            freeInput={{ maxLength: FIELD_MAX_LENGTHS.OPPORTUNITY_INFORMATION_SOURCE, showCharCount: true }}
          />

          <AppTextarea
            id='description'
            label='オポチュニティの詳細'
            value={formData.description || ''}
            onChange={handleDescriptionChange}
            placeholder='詳細な説明を入力'
            rows={5}
            maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION}
            showCharCount
            limitHeight='md'
            error={errors.description}
          />

          {/* TODO: 手入力で範囲外を指定した場合、文字を赤くする。また、その時作成ボタンにバリデーションが効いていないので対応する (作成には正しく失敗するので取り急ぎこの状態) */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <AppInput
              id='startOn'
              label='案件の開始予定日'
              type='date'
              helpText='前後1年間で選択可能'
              value={formData.startOn || ''}
              onChange={(e) => updateFormData({ startOn: e.target.value || null })}
              min={dateConstraints.min}
              max={dateConstraints.max}
            />

            <AppInput
              id='contactName'
              label='取引先責任者'
              value={formData.contactName || ''}
              onChange={(e) => updateFormData({ contactName: e.target.value || null })}
              placeholder='取引先の担当者名'
              maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_CONTACT_NAME}
              showCharCount
            />
          </div>
        </div>
      </div>
    </AppDialog>
  )
}
